import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { 
    Sun, Moon, LogOut, User, Bell, Menu,
    LayoutDashboard, FileText, Calendar, BarChart3, Settings, BookOpen, FolderDown
} from 'lucide-react';
import InstallAppButton from './common/InstallAppButton';

const LOGO_URL = '/logo.png';

const AppHeader = ({ title, showNav = true }) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const [showMobileNav, setShowMobileNav] = React.useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const navItems = [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, roles: ['doctor', 'primary_care', 'consultant', 'institution_admin', 'nhs_manager', 'super_admin'] },
        { label: 'Guidelines', path: '/guidelines', icon: BookOpen, roles: ['doctor', 'primary_care', 'consultant', 'institution_admin', 'nhs_manager', 'super_admin'] },
        { label: 'Consultant', path: '/consultant', icon: FileText, roles: ['consultant', 'institution_admin'] },
        { label: 'Calendar', path: '/consultant/calendar', icon: Calendar, roles: ['consultant', 'institution_admin'] },
        { label: 'Analytics', path: '/analytics', icon: BarChart3, roles: ['institution_admin', 'nhs_manager', 'super_admin'] },
        { label: 'Admin', path: '/admin', icon: Settings, roles: ['super_admin'] },
        { label: 'Resources', path: '/resources', icon: FolderDown, roles: ['doctor', 'primary_care', 'consultant', 'institution_admin', 'nhs_manager', 'super_admin'] },
    ];

    const userNavItems = navItems.filter(item => item.roles.includes(user?.role));

    return (
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo & Title */}
                    <div className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="FendanaCor" className="h-8 w-8 rounded-lg" />
                        <div>
                            <h1 className="text-lg font-bold text-foreground">{title || 'FendanaCor'}</h1>
                            <p className="text-xs text-muted-foreground hidden sm:block">Orthopaedic Referral System</p>
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    {showNav && (
                        <nav className="hidden md:flex items-center gap-1">
                            {userNavItems.map(item => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/10 rounded-lg transition-colors"
                                >
                                    <item.icon className="w-4 h-4" />
                                    {item.label}
                                </Link>
                            ))}
                        </nav>
                    )}

                    {/* Right side controls */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        {/* Install App (PWA) — shown when installable / on iOS Safari */}
                        <InstallAppButton />

                        {/* Notifications */}
                        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                            <Bell className="w-5 h-5" />
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                                0
                            </span>
                        </Button>

                        {/* Dark Mode Toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle-btn"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>

                        {/* User Menu */}
                        <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-border">
                            <Link
                                to="/profile"
                                className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-accent/10 transition-colors group"
                                data-testid="profile-link"
                                title="Profile & Security"
                            >
                                <div className="text-right">
                                    <p className="text-sm font-medium text-foreground group-hover:text-teal-700">
                                        {user?.full_name || user?.email}
                                    </p>
                                    <p className="text-xs text-muted-foreground capitalize flex items-center justify-end gap-1">
                                        <span>{user?.role?.replace('_', ' ')}</span>
                                        <User className="w-3 h-3" />
                                    </p>
                                </div>
                            </Link>
                            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" title="Sign out">
                                <LogOut className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Mobile Menu Toggle */}
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="md:hidden"
                            onClick={() => setShowMobileNav(!showMobileNav)}
                        >
                            <Menu className="w-5 h-5" />
                        </Button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {showMobileNav && (
                    <nav className="md:hidden py-4 border-t border-border">
                        <div className="flex flex-col gap-1">
                            {userNavItems.map(item => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setShowMobileNav(false)}
                                    className="flex items-center gap-3 px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent/10 rounded-lg transition-colors"
                                >
                                    <item.icon className="w-5 h-5" />
                                    {item.label}
                                </Link>
                            ))}
                            <div className="mt-2 pt-2 border-t border-border">
                                <Link
                                    to="/profile"
                                    onClick={() => setShowMobileNav(false)}
                                    className="flex items-center gap-3 px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent/10 rounded-lg transition-colors"
                                    data-testid="mobile-profile-link"
                                >
                                    <User className="w-5 h-5" />
                                    Profile &amp; Security
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center gap-3 px-3 py-2 w-full text-left text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                >
                                    <LogOut className="w-5 h-5" />
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    </nav>
                )}
            </div>
        </header>
    );
};

export default AppHeader;
